import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDeploy } from '../contexts/DeployContext';

function formatValue(v) {
  return typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '—');
}

function FeedRow({ feed, showWeightCol = false }) {
  if (!feed) return null;
  return (
    <TableRow>
      <TableCell>{formatValue(feed.algo_id)}</TableCell>
      <TableCell>{formatValue(feed.name)}</TableCell>
      <TableCell>{formatValue(feed.feed_type)}</TableCell>
      <TableCell>{formatValue(feed.feed_inputs)}</TableCell>
      {showWeightCol && <TableCell>{formatValue(feed.weight)}</TableCell>}
    </TableRow>
  );
}

function SectionTitleRow({ title, chips }) {
  return (
    <TableRow sx={{ backgroundColor: 'action.hover' }}>
      <TableCell colSpan={5} component="th" sx={{ fontWeight: 600, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <span>{title}</span>
          {chips && chips.length > 0 && chips}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function ConfigCard({ config, onEdit, onDelete, onDuplicate }) {
  const { deployApi } = useDeploy();
  const [feedsById, setFeedsById] = useState({});

  useEffect(() => {
    if (!config) {
      setFeedsById({});
      return;
    }

    let cancelled = false;

    deployApi
      .listFeedAlgos()
      .then((res) => {
        if (cancelled) return;
        const items = res?.feed_algos ?? [];
        const map = {};
        items.forEach((f) => {
          if (f && f.algo_id != null) {
            map[f.algo_id] = f;
          }
        });
        setFeedsById(map);
      })
      .catch(() => {
        if (!cancelled) {
          setFeedsById({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [deployApi, config]);

  const getHydratedFeed = (algoId) => {
    if (algoId == null) return null;
    const base = feedsById[Number(algoId)];
    return {
      algo_id: algoId,
      name: base?.name ?? `Feed algo #${algoId}`,
      feed_type: base?.feed_type ?? '—',
      feed_inputs: base?.feed_inputs ?? '—',
    };
  };

  if (!config) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Select a Feed Config to view details
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const cfg = config.feed_config ?? {};
  const mainAlgos = cfg.main_algos ?? [];
  const mainMaxCacheS = cfg.main_max_cache_age_s ?? 7200;
  const primaryMaxCacheS = cfg.primary_max_cache_age_s ?? 7200;
  const secondaryMaxCacheS = cfg.secondary_max_cache_age_s ?? 600;
  const hydratedMainFeeds = mainAlgos.map((c) => {
    const algoId = c.algo_id;
    const base = getHydratedFeed(algoId);
    return {
      ...(base || { algo_id: algoId, name: `Feed algo #${algoId}`, feed_type: '—', feed_inputs: '—' }),
      weight: c.weight ?? '—',
    };
  });
  const primaryId = cfg.primary_fallback;
  const secondaryId = cfg.secondary_fallback;
  const primaryFeed = getHydratedFeed(primaryId);
  const secondaryFeed = getHydratedFeed(secondaryId);

  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
          <Typography variant="h6" component="h2">
            #{config.config_id}: {config.name}
          </Typography>
        </Box>

        <Box sx={{ px: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`Feed Timeout: ${formatValue(cfg.building_timeout_ms ?? 250)}ms`} size="small" variant="outlined" />
            <Chip label={`Page: ${formatValue(cfg.page_size ?? '—')}`} size="small" variant="outlined" />
            <Chip label={config.is_public ? 'Public' : 'Private'} size="small" variant="outlined" />
          </Box>

          {config.description && (
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
          )}

          {(hydratedMainFeeds.length > 0 || primaryFeed || secondaryFeed) && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Feed ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Feed type</TableCell>
                    <TableCell>Feed inputs</TableCell>
                    <TableCell>Weight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hydratedMainFeeds.length > 0 && (
                    <>
                      <SectionTitleRow
                        title="Main feed algos"
                        chips={[
                          <Chip key="main-cache" label={`Max cache: ${formatValue(mainMaxCacheS)}s`} size="small" variant="outlined" />,
                        ]}
                      />
                      {hydratedMainFeeds.map((c, idx) => (
                        <FeedRow key={c.algo_id ?? idx} feed={c} showWeightCol />
                      ))}
                    </>
                  )}
                  {primaryFeed && (
                    <>
                      <SectionTitleRow
                        title="Primary fallback feed"
                        chips={[
                          <Chip key="primary-cache" label={`Max cache: ${formatValue(primaryMaxCacheS)}s`} size="small" variant="outlined" />,
                        ]}
                      />
                      <FeedRow feed={{ ...primaryFeed, weight: '—' }} showWeightCol />
                    </>
                  )}
                  {secondaryFeed && (
                    <>
                      <SectionTitleRow
                        title="Secondary fallback feed"
                        chips={[
                          <Chip key="secondary-cache" label={`Max cache: ${formatValue(secondaryMaxCacheS)}s`} size="small" variant="outlined" />,
                        ]}
                      />
                      <FeedRow feed={{ ...secondaryFeed, weight: '—' }} showWeightCol />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {(() => {
            const rules = cfg.exclusion_rules ?? [];
            if (rules.length === 0) return null;
            const labels = rules.map((r) => {
              if (r.rule === 'max_items_with_same_field') {
                return `Max ${r.value ?? '—'} items with same ${r.field ?? 'field'}`;
              }
              if (r.rule === 'min_distance_between_items_with_same_field') {
                return `Min distance ${r.distance ?? '—'} between items with same ${r.field ?? 'field'}`;
              }
              if (r.rule === 'hide_items_with_same_field') {
                return `Hide items with same ${r.field ?? 'field'}`;
              }
              if (r.rule === 'exclude_items_shown_n_times') {
                return `Exclude items shown ≥${r.value ?? '—'} times`;
              }
              if (r.rule === 'exclude_items_shown_last_n_seconds') {
                return `Exclude items shown in last ${r.value ?? '—'}s`;
              }
              return null;
            }).filter(Boolean);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Exclusion rules
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, typography: 'body2', color: 'text.secondary' }}>
                  {labels.map((label, i) => (
                    <li key={i}>{label}</li>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', gap: 1, px: 2, py: 1.5 }}>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete?.(config)}
        >
          Delete
        </Button>
        <Button
          size="small"
          variant="text"
          color="primary"
          startIcon={<ContentCopyIcon />}
          onClick={() => {
            const { config_id, ...rest } = config;
            onDuplicate?.({ ...rest, name: config.name ? `Copy of ${config.name}` : 'Copy of Feed Config' });
          }}
        >
          Duplicate
        </Button>
        <Button
          size="small"
          variant="text"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => onEdit?.(config)}
        >
          Edit
        </Button>
      </CardActions>
    </Card>
  );
}

export default ConfigCard;
